import React from "react";
import { AlertTriangle } from "lucide-react";
import type { FeedbackItem } from "../types";
import { severityClass, formatSeverity } from "../utils";
import { useLanguage } from "@/i18n/LanguageContext";

export const FeedbackAlertPanel: React.FC<{ items: FeedbackItem[] }> = React.memo(({ items }) => {
  const { t } = useLanguage();
  const highCount = items.filter((item) => item.severity === "high").length;
  const mediumCount = items.filter((item) => item.severity === "medium").length;
  const newCount = items.filter((item) => item.severity === "new_reporting").length;

  return (
    <aside className="relative lg:sticky lg:top-2 lg:w-full lg:max-w-[220px] lg:justify-self-end">
        <section className="card border-red-200/60 p-0 text-[10px]">
          <div className="border-b border-red-200/60 px-3 py-3">
            <div className="flex items-start gap-2">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-600">
                <AlertTriangle className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="text-xs font-semibold text-red-800">{t("dash.feedback.title")}</h2>
                <p className="mt-0.5 text-[9px] text-red-600">
                  {t("dash.feedback.subtitle")}
                </p>
              </div>
            </div>

            <div className="mt-2 grid grid-cols-3 gap-1 text-center">
              <div className="rounded-xl border border-red-200/60 bg-white px-1.5 py-1 shadow-soft">
                <p className="text-[9px] font-semibold uppercase text-red-600">{t("dash.severity.high")}</p>
                <p className="text-xs font-bold text-red-800">{highCount}</p>
              </div>
              <div className="rounded-xl border border-amber-200/60 bg-white px-2 py-1.5 shadow-soft">
                <p className="text-[10px] font-semibold uppercase text-amber-700">{t("dash.severity.medium")}</p>
                <p className="text-xs font-bold text-amber-800">{mediumCount}</p>
              </div>
              <div className="rounded-xl border border-primary-200/60 bg-white px-2 py-1.5 shadow-soft">
                <p className="text-[10px] font-semibold uppercase text-primary-600">{t("dash.feedback.new")}</p>
                <p className="text-xs font-bold text-primary-700">{newCount}</p>
              </div>
            </div>
          </div>

          <div className="max-h-[calc(100vh-180px)] overflow-y-auto px-2 py-2">
            {items.length === 0 ? (
              <div className="rounded-2xl border border-red-100/60 bg-white/80 px-3 py-4 text-center text-xs text-slate-400">
                {t("dash.feedback.empty")}
              </div>
            ) : (
              <div className="space-y-2">
                {items.map((item) => (
                  <article key={item.id} className="rounded-xl border border-red-100/60 bg-white px-2.5 py-2 shadow-soft">
                    <div className="mb-0.5 flex items-center justify-between gap-1">
                      <span className="text-[9px] font-medium uppercase tracking-wide text-slate-500">
                        {item.type}
                      </span>
                      <span className={`rounded-full border px-1.5 py-0 text-[9px] font-medium ${severityClass(item.severity)}`}>
                        {item.severity === "new_reporting" ? t("dash.severity.new") : formatSeverity(item.severity, t)}
                      </span>
                    </div>
                    <h3 className="text-[10px] font-semibold leading-snug text-slate-800">{item.title}</h3>
                    <p className="mt-0.5 text-[9px] leading-snug text-slate-500">{item.detail}</p>
                    <a
  href={`mailto:?subject=${encodeURIComponent(t("dash.feedback.mail.subject", { title: item.title }))}&body=${encodeURIComponent(`${t("dash.feedback.mail.type", { type: item.type })}
${t("dash.feedback.mail.severity", { severity: item.severity === "new_reporting" ? t("dash.severity.new") : formatSeverity(item.severity, t) })}
${item.title}

${item.detail}`)}`}
  className="mt-2 block rounded-xl border border-dashed border-red-200/60 bg-red-200/80 px-2 py-1.5 text-center text-[11px] font-medium text-red-700 hover:bg-red-100 hover:border-red-300/80 transition-all shadow-soft"
>
  {t("dash.feedback.action")}
</a>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
    </aside>
  );
});
FeedbackAlertPanel.displayName = "FeedbackAlertPanel";
